/**
 * Phase O.3 — Admin Lease tab.
 *
 * Read-only observability surface for the lease-based pipeline rollout.
 * Renders four stat cards (opted-in / active / expired_to_reap / drift),
 * the active-leases table, and the parity-drift table.
 *
 * Pure GET endpoints; no writes from this component. Polls every 10s
 * while the tab is mounted, paused when the browser tab is hidden.
 */
import { useEffect, useRef, useState } from "react";
import { Activity, AlertTriangle, CheckCircle2, Hourglass, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const LEASE_UI_ENABLED = process.env.REACT_APP_LEASE_UI === "true";

const StatCard = ({ label, value, tone = "neutral", icon: Icon, testId }) => {
    const toneCls = {
        ok: "border-emerald-500/40 bg-emerald-500/5 text-emerald-300",
        warn: "border-amber-500/40 bg-amber-500/5 text-amber-300",
        alert: "border-red-500/40 bg-red-500/8 text-red-300",
        neutral: "border-zinc-800 bg-zinc-950 text-zinc-200",
    }[tone];
    return (
        <div data-testid={testId} className={`p-4 border rounded-sm ${toneCls}`}>
            <div className="flex items-center gap-2">
                {Icon && <Icon className="w-4 h-4" />}
                <div className="label-micro">{label}</div>
            </div>
            <div className="mt-2 font-display text-3xl">{value}</div>
        </div>
    );
};

const _fmtIso = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleTimeString();
    } catch {
        return iso;
    }
};

export default function LeaseAdminTab() {
    const [health, setHealth] = useState(null);
    const [active, setActive] = useState(null);
    const [drift, setDrift] = useState(null);
    const [err, setErr] = useState("");
    const [refreshing, setRefreshing] = useState(false);
    const tickRef = useRef(null);

    const load = async () => {
        setRefreshing(true);
        try {
            const [h, a, d] = await Promise.all([
                api.get("/admin/lease/health"),
                api.get("/admin/lease/active"),
                api.get("/admin/lease/parity-drift"),
            ]);
            setHealth(h.data);
            setActive(a.data?.active_leases || []);
            setDrift(d.data?.parity_drift || []);
            setErr("");
        } catch (e) {
            setErr(e?.userMessage || e?.message || "Failed to load lease data");
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        load();
        tickRef.current = setInterval(() => {
            if (document.visibilityState === "visible") load();
        }, 10_000);
        return () => clearInterval(tickRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!LEASE_UI_ENABLED) {
        return (
            <div data-testid="lease-admin-disabled" className="p-6 text-sm text-zinc-400 border border-zinc-800 rounded-sm">
                Lease UI is disabled. Set <code className="text-amber-300">REACT_APP_LEASE_UI=true</code> in
                the frontend env to enable.
            </div>
        );
    }

    return (
        <div data-testid="lease-admin-tab" className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="text-xs font-mono text-zinc-500">
                    {health?.sampled_at ? `Sampled ${_fmtIso(health.sampled_at)}` : "Loading…"}
                    {health && (
                        <span className="ml-3">
                            env flag:{" "}
                            <span className={health.lease_pipeline_env_flag ? "text-amber-300" : "text-zinc-400"}>
                                {String(health.lease_pipeline_env_flag)}
                            </span>
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={refreshing}
                    data-testid="lease-admin-refresh-btn"
                    className="text-xs uppercase tracking-wider px-3 py-1.5 border border-zinc-800 hover:border-zinc-600 rounded-sm disabled:opacity-50"
                >
                    <RefreshCw className={`w-3 h-3 inline mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            {err && (
                <div data-testid="lease-admin-error" className="p-3 border border-red-500/40 bg-red-500/8 text-red-300 text-sm rounded-sm">
                    {err}
                </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    testId="lease-stat-opted-in"
                    label="Projects opted in"
                    value={health?.projects_opted_in ?? "—"}
                    icon={Activity}
                />
                <StatCard
                    testId="lease-stat-active"
                    label="Active leases"
                    value={health?.active_leases ?? "—"}
                    tone={health?.active_leases > 0 ? "warn" : "neutral"}
                    icon={Hourglass}
                />
                <StatCard
                    testId="lease-stat-expired"
                    label="Expired to reap"
                    value={health?.expired_to_reap ?? "—"}
                    tone={health?.expired_to_reap > 0 ? "warn" : "neutral"}
                />
                <StatCard
                    testId="lease-stat-drift"
                    label="Parity drift"
                    value={health?.parity_mismatches ?? "—"}
                    tone={
                        health?.parity_mismatches > 0
                            ? "alert"
                            : health
                                ? "ok"
                                : "neutral"
                    }
                    icon={health?.parity_mismatches > 0 ? AlertTriangle : CheckCircle2}
                />
            </div>

            {/* Drift table — only when non-empty */}
            {drift && drift.length > 0 && (
                <div data-testid="lease-drift-table" className="border border-red-500/40 bg-red-500/5 rounded-sm">
                    <div className="p-3 text-red-300 text-sm font-medium border-b border-red-500/40 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Parity drift detected — investigate before next opt-in
                    </div>
                    <table className="w-full text-xs">
                        <thead className="bg-red-500/10 text-red-300 text-[10px] uppercase tracking-wider">
                            <tr>
                                <th className="text-left p-2">Project</th>
                                <th className="text-left p-2">Mismatches</th>
                                <th className="text-left p-2">Persisted</th>
                                <th className="text-left p-2">Derived</th>
                            </tr>
                        </thead>
                        <tbody>
                            {drift.map(row => (
                                <tr key={row.project_id} className="border-t border-red-500/30">
                                    <td className="p-2 font-mono">{row.project_title || row.project_id}</td>
                                    <td className="p-2">{(row.mismatches || []).join("; ")}</td>
                                    <td className="p-2 font-mono">{JSON.stringify(row.persisted)}</td>
                                    <td className="p-2 font-mono">{JSON.stringify(row.derived)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Active leases table */}
            <div className="card-base overflow-hidden">
                <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wider text-zinc-400">
                        Active leases ({active?.length ?? 0})
                    </div>
                </div>
                {active === null ? (
                    <div className="p-6 text-zinc-500 text-sm">Loading…</div>
                ) : active.length === 0 ? (
                    <div data-testid="lease-active-empty" className="p-6 text-zinc-500 text-sm">
                        No active leases. The system is idle.
                    </div>
                ) : (
                    <table className="w-full text-xs" data-testid="lease-active-table">
                        <thead className="bg-zinc-900 text-zinc-400 text-[10px] uppercase tracking-wider">
                            <tr>
                                <th className="text-left p-2">Project</th>
                                <th className="text-left p-2">Stage</th>
                                <th className="text-left p-2">Lease ID</th>
                                <th className="text-left p-2">Attempt</th>
                                <th className="text-left p-2">Source</th>
                                <th className="text-left p-2">Acquired</th>
                                <th className="text-left p-2">Expires in</th>
                                <th className="text-left p-2">Renewed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {active.map(row => (
                                <tr key={row.lease_id} className="border-t border-zinc-900">
                                    <td className="p-2">{row.project_title || row.project_id}</td>
                                    <td className="p-2 font-mono">{row.stage_key}</td>
                                    <td className="p-2 font-mono text-zinc-500">{String(row.lease_id).slice(0, 8)}…</td>
                                    <td className="p-2">{row.attempt}</td>
                                    <td className="p-2 text-zinc-400">{row.source}</td>
                                    <td className="p-2 text-zinc-400">{_fmtIso(row.acquired_at)}</td>
                                    <td className={`p-2 ${row.expires_in_seconds < 30 ? "text-amber-300" : ""}`}>
                                        {row.expires_in_seconds}s
                                    </td>
                                    <td className="p-2 text-zinc-400">{_fmtIso(row.last_renewed_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
